<template>
  <div
    :class="[
      'sb-datepicker',
      isFullWidth && 'sb-datepicker--full-width',
      attrs?.class ?? '',
    ]"
    :data-testid="baseDataTestId"
  >
    <label
      v-if="label"
      :id="`${uid}-label`"
      :for="inputId || `${uid}-input`"
      class="sb-datepicker__label"
    >
      {{ label }}
      <span v-if="required" class="sb-datepicker__required" aria-hidden="true">
        *
      </span>
    </label>
    <div class="sb-datepicker__input-wrapper">
      <Calendar
        :id="`${uid}-datepicker`"
        ref="calendarRef"
        :model-value="displayDateValue"
        :date-format="adaptiveDateFormat"
        :min-date="minDate"
        :max-date="maxDate"
        :manual-input="props.manualInput"
        :readonly="disabled"
        :placeholder="placeholderWithFormat"
        :append-to="appendTo"
        show-icon
        :show-button-bar="showButtonBar"
        icon-display="input"
        :invalid="!!errorMessage"
        :input-props="{
          ...rootAttrs,
          'aria-describedby': `${uid}-message`,
          'aria-disabled': disabled,
          'aria-labelledby': inputId ? undefined : `${uid}-label`,
          id: inputId || `${uid}-input`,
        }"
        :data-testid="`${baseDataTestId}-input`"
        :class="{ 'sb-datepicker__calendar': true }"
        @update:model-value="handleDateChange"
      />
      <SbIconButton
        v-if="showClearButton && !!displayDateValue"
        size="medium"
        icon="circle-x"
        :label="`Clear ${label || 'date'}`"
        class="sb-datepicker__clear-btn"
        @click="handleClear"
      />
    </div>
    <Transition name="sb-datepicker-fade" appear>
      <div
        v-if="showTimePicker && !!displayDateValue"
        class="sb-datepicker__time-picker"
      >
        <SbTextField
          :id="`${uid}-time`"
          label="Set Time"
          :model-value="currentTimeValue"
          :data-testid="`${baseDataTestId}-time-input`"
          type="time"
          mask="##:##"
          placeholder="HH:mm"
          pattern="^[0-9:]*$"
          :disabled="disabled"
          class="sb-datepicker__time-input"
          :suffix="timeSuffix"
          @update:model-value="onTimeChange"
          @blur="onTimeBlur"
        />
        <SbSelect
          v-if="timezoneEditable"
          v-model="selectedTimezone"
          :input-id="`${uid}-timezone`"
          :options="timezonesWithDynamicOffsets"
          :is-disabled="disabled"
          class="sb-datepicker__timezone-select"
          placeholder="Select timezone"
          :hidden-label="`Select timezone for ${label}`"
          :data-testid="`${baseDataTestId}-timezone-select`"
          @update:model-value="onTimezoneChange"
        />
      </div>
    </Transition>
    <p
      v-if="!!errorMessage || !!description"
      :id="`${uid}-message`"
      :class="{
        'sb-datepicker__message': true,
        'sb-datepicker__message--error': !!errorMessage,
        'sb-datepicker__message--description': !!description,
      }"
    >
      {{ errorMessage || description }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import {
  computed,
  nextTick,
  ref,
  useAttrs,
  useId,
  watch,
  type InputHTMLAttributes,
} from 'vue'

import {
  format,
  parse,
  parseISO,
  setHours,
  setMinutes,
  getHours,
  getMinutes,
  isValid,
} from 'date-fns'
import { fromZonedTime, toZonedTime, formatInTimeZone } from 'date-fns-tz'
import Calendar from 'primevue/calendar'

import { SbSelect } from '../Select'
import { SbTextField } from '../TextField'
import { SbIconButton } from '../IconButton'

import {
  TIMEZONES_LIST as timezonesList,
  getDateFormatForTimezone,
} from '../../utils'
import { DATE_FORMATS } from '../../utils/constants'

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  (e: 'update:model-value', value: string | undefined): void
  (e: 'date-format-changed', value: string, timezone: string): void
}>()

type DatepickerProps = {
  modelValue?: string | undefined
  dateFormat?: string
  placeholder?: string

  minDate?: Date
  maxDate?: Date

  timezone?: string
  showTimePicker?: boolean
  timezoneEditable?: boolean
  manualInput?: boolean
  showClearButton?: boolean

  label?: string
  required?: boolean
  disabled?: boolean
  errorMessage?: string
  description?: string

  isFullWidth?: boolean
  inputId?: string
  appendTo?: string
  showButtonBar?: boolean
} & /* @vue-ignore */ InputHTMLAttributes

const props = withDefaults(defineProps<DatepickerProps>(), {
  showTimePicker: false,
  timezoneEditable: false,
  manualInput: true,
  showClearButton: true,
})

const attrs = useAttrs()

const rootAttrs = reactiveOmit(attrs, ['data-testid', 'class'])

const calendarRef = ref()

function closeCalendar() {
  if (calendarRef.value && calendarRef.value.overlayVisible) {
    calendarRef.value.overlayVisible = false
  }
}

const handleClear = () => {
  emit('update:model-value', undefined)
  visualDateTime.value = undefined
  currentTimeValue.value = ''
}

defineExpose({
  closeCalendar,
  calendarRef,
})

const errorMessage = computed(() => {
  if (props.required && !props.modelValue) {
    return 'This field is required'
  }
  return props.errorMessage
})

const selectedTimezone = ref('')
const effectiveTimezone = computed(() => selectedTimezone.value || 'UTC')

const visualDateTime = ref<Date | undefined>(undefined)
const currentTimeValue = ref('')
const isUserTyping = ref(false)

const timeSuffix = computed((): string => {
  if (!props.timezoneEditable && selectedTimezone.value) {
    const tz = timezonesWithDynamicOffsets.value.find(
      (tz) => tz.value === selectedTimezone.value,
    )
    return tz ? `UTC${tz.offset}` : 'UTC+00:00'
  }
  return ''
})

const emitDateTimeValue = (date: Date) => {
  if (!isValid(date)) {
    console.warn('[SbDatepicker] Invalid date passed to emitDateTimeValue:', {
      date,
      component: 'SbDatepicker',
    })
    return
  }

  try {
    const utcTime = fromZonedTime(date, effectiveTimezone.value)
    emit('update:model-value', utcTime.toISOString())
  } catch (error) {
    emit('update:model-value', date.toISOString())
  }
}

const isISOString = (value: string) =>
  value.includes('T') && value.includes('Z')

const displayDateValue = computed((): Date | undefined => {
  if (visualDateTime.value) {
    return visualDateTime.value
  }
  if (!props.modelValue || typeof props.modelValue !== 'string') {
    return undefined
  }

  let parsedDate: Date | undefined
  if (isISOString(props.modelValue)) {
    parsedDate = parseISO(props.modelValue)
  } else {
    if (!props.showTimePicker) {
      if (props.modelValue.includes(':')) {
        const dateOnly = props.modelValue.split(' ')[0]
        return dateOnly ? parse(dateOnly, 'yyyy-MM-dd', new Date()) : undefined
      } else {
        return parse(props.modelValue, 'yyyy-MM-dd', new Date())
      }
    }

    parsedDate = parse(props.modelValue, 'yyyy-MM-dd HH:mm', new Date())
  }

  if (!isValid(parsedDate)) {
    console.warn('[SbDatepicker] Invalid date provided:', {
      modelValue: props.modelValue,
      parsedDate,
      component: 'SbDatepicker',
    })
    return undefined
  }

  if (props.showTimePicker) {
    try {
      const initialTimezone = props.timezone || 'UTC'
      const zonedDate = toZonedTime(parsedDate, initialTimezone)
      visualDateTime.value = zonedDate
      if (!isUserTyping.value) {
        currentTimeValue.value = format(zonedDate, 'HH:mm')
      }
      return zonedDate
    } catch (error) {
      visualDateTime.value = parsedDate
      if (!isUserTyping.value) {
        currentTimeValue.value = format(parsedDate, 'HH:mm')
      }
      return parsedDate
    }
  }
  visualDateTime.value = parsedDate
  return parsedDate
})

const baseDataTestId = computed(() => attrs['data-testid'] || 'sb-datepicker')

const handleDateChange = (
  newDate: Date | Date[] | (Date | null)[] | null | undefined,
) => {
  if (!newDate) {
    emit('update:model-value', undefined)
    visualDateTime.value = undefined
    currentTimeValue.value = ''
    return
  }

  const selectedDate = Array.isArray(newDate) ? newDate[0] : newDate
  if (!selectedDate) {
    emit('update:model-value', undefined)
    visualDateTime.value = undefined
    currentTimeValue.value = ''
    return
  }

  let finalDate = selectedDate
  if (props.showTimePicker && visualDateTime.value) {
    const currentHour = getHours(visualDateTime.value)
    const currentMinute = getMinutes(visualDateTime.value)
    finalDate = setMinutes(setHours(selectedDate, currentHour), currentMinute)
  }
  visualDateTime.value = finalDate
  if (props.showTimePicker) {
    currentTimeValue.value = format(finalDate, 'HH:mm')
    validateAndCorrectTime(finalDate)
  } else {
    emitDateTimeValue(finalDate)
  }
}

const isDateTimeBeforeMin = (dateToCheck: Date): boolean => {
  if (!props.minDate) {
    return false
  }

  try {
    const minDateInTimezone = toZonedTime(
      props.minDate,
      effectiveTimezone.value,
    )
    const dateToCheckInTimezone = toZonedTime(
      dateToCheck,
      effectiveTimezone.value,
    )

    return dateToCheckInTimezone < minDateInTimezone
  } catch (error) {
    return dateToCheck < props.minDate
  }
}

const getMinTimeForDate = (
  date: Date,
): { hours: number; minutes: number } | null => {
  if (!props.minDate) {
    return null
  }

  try {
    const minDateInTimezone = toZonedTime(
      props.minDate,
      effectiveTimezone.value,
    )
    const dateInTimezone = toZonedTime(date, effectiveTimezone.value)

    const dateOnly = format(dateInTimezone, 'yyyy-MM-dd')
    const minDateOnly = format(minDateInTimezone, 'yyyy-MM-dd')

    if (dateOnly === minDateOnly) {
      return {
        hours: getHours(minDateInTimezone),
        minutes: getMinutes(minDateInTimezone),
      }
    }
  } catch (error) {
    const dateOnly = format(date, 'yyyy-MM-dd')
    const minDateOnly = format(props.minDate, 'yyyy-MM-dd')

    if (dateOnly === minDateOnly) {
      return {
        hours: getHours(props.minDate),
        minutes: getMinutes(props.minDate),
      }
    }
  }

  return null
}

const validateAndCorrectTime = (dateToValidate: Date) => {
  if (isDateTimeBeforeMin(dateToValidate)) {
    const minTime = getMinTimeForDate(dateToValidate)
    if (minTime) {
      const correctedDate = setMinutes(
        setHours(dateToValidate, minTime.hours),
        minTime.minutes,
      )
      visualDateTime.value = correctedDate
      currentTimeValue.value = format(correctedDate, 'HH:mm')
      emitDateTimeValue(correctedDate)
      return correctedDate
    }
  }
  emitDateTimeValue(dateToValidate)
  return dateToValidate
}

const onTimeChange = (newTime: string | number | null | undefined) => {
  if (typeof newTime !== 'string' || !visualDateTime.value) {
    return
  }

  isUserTyping.value = true
  currentTimeValue.value = newTime

  if (newTime.length < 5) {
    return
  }
}

const onTimeBlur = () => {
  isUserTyping.value = false
  if (!visualDateTime.value) {
    return
  }

  const newTime = currentTimeValue.value

  if (newTime === '') {
    const updatedDate = setMinutes(setHours(visualDateTime.value, 0), 0)
    visualDateTime.value = updatedDate
    validateAndCorrectTime(updatedDate)
    return
  }

  const timeMatch = newTime.match(/^(\d{1,2}):(\d{2})$/)

  if (!timeMatch || !timeMatch[1] || !timeMatch[2]) {
    currentTimeValue.value = format(visualDateTime.value, 'HH:mm')
    return
  }

  const hours = parseInt(timeMatch[1], 10)
  const minutes = parseInt(timeMatch[2], 10)

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    currentTimeValue.value = format(visualDateTime.value, 'HH:mm')
    return
  }

  const updatedDate = setMinutes(setHours(visualDateTime.value, hours), minutes)
  visualDateTime.value = updatedDate
  const finalDate = validateAndCorrectTime(updatedDate)

  nextTick(() => {
    currentTimeValue.value = format(finalDate, 'HH:mm')
  })
}

const onTimezoneChange = (value: string | { value: string } | undefined) => {
  if (!value) {
    return
  }
  const newTimezone = typeof value === 'string' ? value : value.value
  const oldTimezone = selectedTimezone.value || 'UTC'
  selectedTimezone.value = newTimezone
  if (visualDateTime.value) {
    try {
      const zonedDate = toZonedTime(
        fromZonedTime(visualDateTime.value, oldTimezone),
        newTimezone,
      )
      visualDateTime.value = zonedDate
      currentTimeValue.value = format(zonedDate, 'HH:mm')
    } catch (error) {
      currentTimeValue.value = format(visualDateTime.value, 'HH:mm')
    }
    emitDateTimeValue(visualDateTime.value)
  }
  if (!props.dateFormat) {
    const newFormat = adaptiveDateFormat.value
    emit('date-format-changed', newFormat, newTimezone)
  }
}

const uid = `sb-datepicker-${useId()}`

const adaptiveDateFormat = computed(() => {
  if (props.dateFormat) {
    return props.dateFormat
  }

  if (selectedTimezone.value) {
    const timezoneMapping = getDateFormatForTimezone(selectedTimezone.value)
    return timezoneMapping.dateFormat
  }

  return DATE_FORMATS.EUROPEAN
})

const placeholderWithFormat = computed(() => {
  const format = adaptiveDateFormat.value.toUpperCase()
  return props.placeholder
    ? `${props.placeholder} (${format})`
    : `Select date (${format})`
})

const timezonesWithDynamicOffsets = computed(() => {
  return timezonesList.map((timezone) => {
    try {
      let dateForOffset
      if (props.modelValue) {
        dateForOffset =
          props.modelValue.includes('T') && props.modelValue.includes('Z')
            ? parseISO(props.modelValue)
            : parse(props.modelValue, 'yyyy-MM-dd HH:mm', new Date())
      } else {
        dateForOffset = new Date()
      }

      if (!isValid(dateForOffset)) {
        throw new Error('Invalid date for offset calculation')
      }

      const dynamicOffset = formatInTimeZone(
        dateForOffset,
        timezone.value,
        'xxx',
      )

      return {
        ...timezone,
        label: `${timezone.label} (UTC${dynamicOffset})`,
        originalLabel: timezone.label,
        offset: dynamicOffset,
      }
    } catch (error) {
      return {
        ...timezone,
        label: `${timezone.label} (UTC${timezone.offset})`,
        originalLabel: timezone.label,
        offset: timezone.offset,
      }
    }
  })
})

watch(
  () => props.timezone,
  (newTimezone: string | undefined) => {
    selectedTimezone.value = newTimezone || 'UTC'
  },
  { immediate: true },
)

watch(
  () => adaptiveDateFormat.value,
  (newFormat, oldFormat) => {
    if (
      !props.dateFormat &&
      newFormat !== oldFormat &&
      selectedTimezone.value
    ) {
      emit('date-format-changed', newFormat, selectedTimezone.value)
    }
  },
)

watch(
  [() => props.modelValue, () => props.minDate],
  () => {
    if (props.showTimePicker && displayDateValue.value && props.minDate) {
      const currentDate = displayDateValue.value
      if (isDateTimeBeforeMin(currentDate)) {
        const minTime = getMinTimeForDate(currentDate)
        if (minTime) {
          const correctedDate = setMinutes(
            setHours(currentDate, minTime.hours),
            minTime.minutes,
          )
          emitDateTimeValue(correctedDate)
        }
      }
    }
  },
  { immediate: true },
)
</script>

<style lang="scss">
@import './datepicker';
</style>
